/** @jsxImportSource theme-ui */

import * as React from 'react';
import { Box, Button, Grid } from 'theme-ui';
import { Link } from 'gatsby';
import { useTranslation } from 'react-i18next';

import type {
  LocalizedLiveCopy,
  PublicLiveSession,
} from '@youga/youga-interfaces';
import {
  getLiveJoinErrorCode,
  formatLiveSessionDate,
  useLiveSessions,
} from '@youga/youga-client-api';

import Text from '../../01_atoms/Text/Text';
import i18n from '../../../i18n/i18n';
import useTracking from '../../../hooks/useTracking';

const localized = (copy: LocalizedLiveCopy): string => {
  const language = i18n.language.split('-')[0] as 'pl' | 'en' | 'de';
  return copy[language] || copy.pl;
};

const formatDate = (value: string): string =>
  formatLiveSessionDate(value, i18n.language);

export function LiveTrainingCard({
  session,
  onJoin,
}: {
  session: PublicLiveSession;
  onJoin: (sessionId: string, isFree: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { track } = useTracking();
  const impressionTracked = React.useRef(false);
  const canJoin = session.joinWindowActive && !session.isFull;

  React.useEffect(() => {
    if (impressionTracked.current) return;
    impressionTracked.current = true;
    void track('live-card-impression', {
      sessionId: session.id,
      startsAt: session.startsAt,
      isFull: session.isFull,
      isFree: session.isFree,
    });
  }, [session.id, session.isFree, session.isFull, session.startsAt, track]);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: ['210px', '250px'],
        borderRadius: '24px',
        overflow: 'hidden',
        backgroundColor: '#b0b1b2',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: session.imageUrl
            ? `url("${session.imageUrl}")`
            : undefined,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          filter: canJoin ? undefined : 'grayscale(1)',
          transform: 'scale(1.01)',
        }}
      />
      {session.isFree ? (
        <Box
          sx={{
            position: 'absolute',
            zIndex: 2,
            top: 3,
            right: 3,
            borderRadius: '999px',
            backgroundColor: 'white',
            px: 3,
            py: 1,
          }}
        >
          <Text sx={{ fontSize: 0, fontWeight: 'bold' }}>{t('LIVE_FREE')}</Text>
        </Box>
      ) : null}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.08) 15%, rgba(0,0,0,0.68) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          minHeight: ['210px', '250px'],
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
          padding: [3, 4],
        }}
      >
        <Text sx={{ color: 'white', mb: 1, fontSize: 1 }}>
          {formatDate(session.startsAt)}
        </Text>
        <Text variant="h3" sx={{ color: 'white', mb: 3 }}>
          {localized(session.title)}
        </Text>
        <Button
          type="button"
          variant="white"
          disabled={!canJoin}
          onClick={() => onJoin(session.id, session.isFree)}
          sx={{
            borderRadius: '999px',
            px: 4,
            opacity: canJoin ? 1 : 0.9,
            cursor: canJoin ? 'pointer' : 'default',
          }}
        >
          {session.isFull
            ? t('LIVE_FULL')
            : canJoin
            ? t('LIVE_JOIN')
            : t('LIVE_AVAILABLE_SOON')}
        </Button>
      </Box>
    </Box>
  );
}

interface LiveTrainingsProps {
  limit?: number;
  showAllLink?: boolean;
  showBalance?: boolean;
}

export default function LiveTrainings({
  limit,
  showAllLink = false,
  showBalance = false,
}: LiveTrainingsProps) {
  const { t } = useTranslation();
  const {
    data: sessions,
    featureEnabled,
    joinLiveSession,
    balance,
    joinedSessionIds,
    refreshBalance,
  } = useLiveSessions();
  const [error, setError] = React.useState<string>();
  const { track } = useTracking();

  React.useEffect(() => {
    if (showBalance) void track('live-list-opened');
  }, [showBalance, track]);

  const onJoin = React.useCallback(
    async (sessionId: string, isFree = false) => {
      if (
        !window.confirm(
          t(
            joinedSessionIds.includes(sessionId)
              ? 'LIVE_REJOIN_CONFIRM'
              : isFree
              ? 'LIVE_FREE_JOIN_CONFIRM'
              : 'LIVE_JOIN_CONFIRM',
          ),
        )
      )
        return;

      setError(undefined);
      try {
        void track('live-join-confirmed', { sessionId, isFree });
        const { joinUrl } = await joinLiveSession(sessionId);
        await refreshBalance();
        window.open(joinUrl, '_blank', 'noopener,noreferrer');
        void track('live-meet-opened', {
          sessionId,
          provider: 'google_meet',
        });
      } catch (joinError) {
        const code = getLiveJoinErrorCode(joinError);
        setError(
          code === 'LIVE_CREDITS_REQUIRED'
            ? t('LIVE_NO_CREDITS')
            : code === 'LIVE_SESSION_FULL'
            ? t('LIVE_FULL')
            : t('LIVE_JOIN_ERROR'),
        );
      }
    },
    [joinLiveSession, joinedSessionIds, refreshBalance, t, track],
  );

  const visibleSessions = limit == null ? sessions : sessions.slice(0, limit);

  if (!featureEnabled) return null;

  return (
    <Box as="section" sx={{ mb: [4, 5] }}>
      <Text variant="h2light" sx={{ fontSize: [3, 4], mt: 0, mb: [3, 4] }}>
        {t('LIVE_TRAININGS')}
      </Text>
      {showBalance ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fff2eb',
            borderRadius: '16px',
            px: 4,
            py: 3,
            mb: 4,
          }}
        >
          <Text>{t('LIVE_BALANCE')}</Text>
          <Text variant="h3">{balance}</Text>
        </Box>
      ) : null}
      {sessions.length === 0 ? (
        <Box
          sx={{
            borderRadius: '20px',
            backgroundColor: '#fff2eb',
            textAlign: 'center',
            px: 4,
            py: 5,
          }}
        >
          <Text>{t('LIVE_EMPTY')}</Text>
        </Box>
      ) : (
        <Grid columns={[1, 2]} gap="1.5rem">
          {visibleSessions.map((session) => (
            <LiveTrainingCard
              key={session.id}
              session={session}
              onJoin={onJoin}
            />
          ))}
        </Grid>
      )}
      {showAllLink && sessions.length > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Link to="/app/live" style={{ textDecoration: 'none' }}>
            <Button variant="whiteBorder">{t('LIVE_SHOW_ALL')}</Button>
          </Link>
        </Box>
      ) : null}
      {error ? (
        <Text role="alert" sx={{ color: 'red', mt: 2 }}>
          {error}
        </Text>
      ) : null}
    </Box>
  );
}
